import { useState } from "react";
import PropTypes from "prop-types";
import { toast } from "react-toastify";
import { useParams } from "react-router-dom";
import { FormattedMessage, useIntl } from "react-intl";

import "./Quotation.scss";
import errorToast from "helpers/errorToast";
import { postAssignTeam } from "api";
import { AssignTeam } from "api/models";
import Dialog from "components/organisms/Dialog/Dialog";
import Button from "components/atoms/Button/Button";
import PillTabs from "components/atoms/PillTabs/PillTabs";
import testIds from "constants/testIds";

interface IAssignDialogProps {
  isOpen: boolean;
  close: () => void;
  assignableTeams: AssignTeam[];
  getQuoteRequest: Function;
  refetchAssignedTeams: Function;
  refetchAssignableTeams: Function;
}

function AssignTeamDialog(props: IAssignDialogProps) {
  const intl = useIntl();
  const { isOpen, close, assignableTeams, getQuoteRequest, refetchAssignedTeams, refetchAssignableTeams } = props;
  const [assigned, setAssigned] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { id } = useParams();

  const handleStatusChange = (eventKey: string) => {
    setAssigned(eventKey);
  };

  // Post assign team detail
  const handleAssignTeam = () => {
    const payload = {
      team_reference: assigned,
    };
    setIsSubmitting(true);
    postAssignTeam(id, payload)
      .then((res) => {
        toast(res.data?.detail, { type: "success" });
        getQuoteRequest();
        refetchAssignableTeams();
        refetchAssignedTeams();
        close();
      })
      .catch((error) => errorToast(error))
      .finally(() => setIsSubmitting(false));
  };

  return (
    <Dialog
      size="md"
      show={isOpen}
      onHide={close}
      title={intl.formatMessage({
        id: "quote.assignToTeam",
        defaultMessage: "Assign to a team",
      })}
    >
      <div style={{ maxWidth: 493 }}>
        <p className="text-secondary mb-0 font-b1">
          <FormattedMessage id="quote.availableTeam" defaultMessage="This request is available for the following teams." />
        </p>
        <p className="text-secondary mb-0 font-b1">
          <FormattedMessage
            id="quote.NeedToAssignTeam"
            defaultMessage="To provide a quote, you will need to assign it to one of the teams."
          />
        </p>
        <p className="text-secondary font-b1">
          <FormattedMessage
            id="quote.assignUnavailableForOtherTeam"
            defaultMessage="Assigning it to a team will make the quote request unavailable to other teams."
          />
        </p>
        <div className="d-flex mt-5 text-center pills-wrapper assign-team-tags">
          <PillTabs
            id="quote-statuses"
            activeKey={assigned}
            onSelect={handleStatusChange}
            tabItems={assignableTeams.map((team) => ({
              title: team.team_name,
              eventKey: team.team_reference,
            }))}
          />
        </div>
        <div className="d-flex justify-content-between pt-4">
          <Button
            className="btn-grey"
            label={intl.formatMessage({
              id: "general.discard",
              defaultMessage: "Discard",
            })}
            onClick={close}
            data-testid={testIds.SHIPMENT_DASHBOARD_ASSIGN_MODAL_CLOSE_BUTTON}
          />
          <Button
            className="ml-2"
            variant="primary"
            onClick={handleAssignTeam}
            data-testid={testIds.SHIPMENT_DASHBOARD_MODAL_ASSIGN_BUTTON}
            disabled={!assigned || isSubmitting}
          >
            <FormattedMessage id="general.assign" defaultMessage="Assign" />
          </Button>
        </div>
      </div>
    </Dialog>
  );
}

AssignTeamDialog.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  close: PropTypes.func.isRequired,
};

export default AssignTeamDialog;
